import { CheckIcon, ExportIcon } from '@phosphor-icons/react';
import { Button, FilterButton, Loader, Search, VText } from '@vake/ui-kit';
import { FC, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useGetReportInfo, useGetVesselsQuery } from '../requests/actor-mode';
import { useSendToMattermost } from '../requests/reports';
import { formatRelativeTime } from '../shared/components/time';
import { allTagsActor, elevatedTags } from '../shared/constants';
import { AbsoluteSpinner } from '../shared/styles/Spinner';
import { isDateIntervalWithinGivenDays } from '../utils/date';
import { getOverwatch2Url } from '../utils/env';
import { ErrorHandler } from '../utils/errorHandling';
import { ActorModeRow } from './Report/components/actor-mode-row';
import { LeftBar } from './Report/components/info-left/info-left';
import { OverwatchContext } from './Report/interactiveReport/overwatch-2-context';
import { initialMapView } from './Report/interactiveReport/overwatch-2-initial-state';
import { Filters, Settings, View } from './Report/interactiveReport/overwatch-2-types';

const verified = 'matched';
const dark = 'dark_vessel';
const unCorrelated = 'undetected';

type RouteParams = {
    reportUuid: string;
};
const ActorMode: FC = () => {
    const params = useParams<RouteParams>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { mutate } = useSendToMattermost();
    const reportUuid = params.reportUuid || '';
    const {
        data: report,
        isError: isErrorReport,
        isLoading: isLoadingReport,
        isSuccess: isSuccessReport,
    } = useGetReportInfo(reportUuid);

    const yesterday = new Date();
    yesterday.setDate(new Date().getDate() - 1);
    yesterday.setHours(0, 0);
    const [startDate, setStartDate] = useState<Date | null>(yesterday);

    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);
    tomorrow.setHours(0, 0);
    const [endDate, setEndDate] = useState<Date | null>(tomorrow);

    const defaultConfidence = 0;
    const [darkDetectionConfidence, setDarkDetectionConfidence] = useState<number>(defaultConfidence);

    const [verifiedActive, setVerifiedActive] = useState<boolean>(false);
    const [darkActive, setDarkActive] = useState<boolean>(false);
    const [unCorrelatedActive, setUnCorrelatedActive] = useState<boolean>(false);

    const tagsFrequency: Record<string, number> = {};
    const [tags, setTags] = useState<FilterButton[]>(
        allTagsActor
            .map((tag) => ({
                children: tag,
                variant: elevatedTags.includes(tag) ? ('warning' as const) : ('neutral' as const),
                size: 'small' as const,
                subtle: true,
                count: tag in tagsFrequency ? tagsFrequency[tag] : undefined,
            }))
            .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    );
    const maxDaysToQuery = 40;
    const [tagFilterStrategy, setTagFilterStrategy] = useState<'or' | 'and'>('or');
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } = useGetVesselsQuery(
        reportUuid,
        startDate,
        endDate,
        verifiedActive,
        darkActive,
        unCorrelatedActive,
        tags
            .filter((tag) => !tag.subtle && typeof tag.children === 'string')
            .map((tag) => (tag.children as string).replace(/ /g, '-').replace(/>/g, 'over')),
        maxDaysToQuery,
        tagFilterStrategy
    );
    const flatMapData = data?.pages.flatMap((page) => page.data) || [];

    const numVerified = flatMapData.filter((row) => row.matchingLabel == verified).length;
    const numDark = flatMapData.filter((row) => row.matchingLabel == dark).length;
    const numUncorrelated = flatMapData.filter((row) => row.matchingLabel == unCorrelated).length;

    const filteredData = flatMapData.filter((row) => {
        let toKeep = true;
        if (row.matchingLabel === dark) {
            toKeep = toKeep && (row.confidence ?? 0) > darkDetectionConfidence / 100;
        }
        if (verifiedActive || darkActive || unCorrelatedActive) {
            // Filtering is needed if one of them is true
            toKeep =
                (toKeep && verifiedActive && row.matchingLabel === verified) ||
                (toKeep && darkActive && row.matchingLabel === dark) ||
                (toKeep && unCorrelatedActive && row.matchingLabel === unCorrelated);
        }
        return toKeep;
    });

    const [selected, setSelected] = useState<number>(0);
    const [updateUrlClicked, setUpdateUrlClicked] = useState<boolean>(false);
    useEffect(() => {
        document.title = isSuccessReport ? report.name : 'Actor';
    }, [report, isSuccessReport]);
    useEffect(() => {
        const spStartDate = searchParams.get('startDate');
        if (spStartDate) {
            setStartDate(new Date(spStartDate));
        }
        const spEndDate = searchParams.get('endDate');
        if (spEndDate) {
            const eD = new Date(spEndDate);
            eD.setHours(23, 59);
            setEndDate(eD);
        }
        const confidence = searchParams.get('confidence');
        if (confidence) {
            setDarkDetectionConfidence(Number(confidence));
        }
        const spTags = searchParams.get('tags');
        if (spTags) {
            const inputTags = spTags.split(',');
            setTags((prevTags) =>
                prevTags.map((tag) => ({
                    ...tag,
                    subtle: !(typeof tag.children === 'string' && inputTags.includes(tag.children)),
                }))
            );
        }
        const spVerifiedActive = searchParams.get('verifiedActive');
        if (spVerifiedActive) {
            setVerifiedActive(spVerifiedActive === 'true' ? true : false);
        }
        const spDarkActive = searchParams.get('darkActive');
        if (spDarkActive) {
            setDarkActive(spDarkActive === 'true' ? true : false);
        }
        const spUnCorrelatedActive = searchParams.get('unCorrelatedActive');
        if (spUnCorrelatedActive) {
            setUnCorrelatedActive(spUnCorrelatedActive === 'true' ? true : false);
        }
        const spTagFilterStrategy = searchParams.get('tagFilterStrategy');
        if (spTagFilterStrategy && (spTagFilterStrategy === 'or' || spTagFilterStrategy === 'and')) {
            setTagFilterStrategy(spTagFilterStrategy);
        }
        const spSelected = searchParams.get('selected');
        if (spSelected) {
            const parsedSelected = parseInt(spSelected, 10);
            if (!isNaN(parsedSelected)) {
                setSelected(parsedSelected);
            }
        }
    }, [searchParams]);

    const updateUrl = () => {
        const newSearchParams = new URLSearchParams();
        if (startDate) {
            newSearchParams.set('startDate', startDate.toDateString());
        }
        if (endDate) {
            newSearchParams.set('endDate', endDate.toDateString());
        }
        newSearchParams.set('confidence', darkDetectionConfidence.toString());
        const saveTags = tags.filter((tag) => !tag.subtle).map((tag) => tag.children);
        if (saveTags.length > 0) {
            newSearchParams.set('tags', saveTags.join(','));
        }
        newSearchParams.set('verifiedActive', String(verifiedActive));
        newSearchParams.set('darkActive', String(darkActive));
        newSearchParams.set('unCorrelatedActive', String(unCorrelatedActive));
        newSearchParams.set('tagFilterStrategy', tagFilterStrategy);
        newSearchParams.set('selected', String(selected));
        setSearchParams(newSearchParams, { replace: true });
        mutate(`Updated Vessel Table [URL](${window.location.href})`);
    };
    const [platformsActive, setPlatformsActive] = useState<boolean>(false);
    const [windmillsActive, setWindmillsActive] = useState<boolean>(false);
    const [openDate, setOpenDate] = useState<boolean>(true);
    const [openConfidence, setOpenConfidence] = useState<boolean>(true);
    const [openDetectionType, setOpenDetectionType] = useState<boolean>(true);
    const [openStaticObjects, setOpenStaticObjects] = useState<boolean>(false);
    const [openTags, setOpenTags] = useState<boolean>(false);
    const filters: Filters = {
        startDate,
        endDate,
        setStartDate: setStartDate,
        setEndDate,
        useDateInterval: true,
        darkDetectionConfidence,
        setDarkDetectionConfidence: setDarkDetectionConfidence,
        defaultConfidence,
        verifiedActive,
        darkActive,
        unCorrelatedActive,
        windmillsActive,
        platformsActive,
        setVerifiedActive,
        setDarkActive,
        setUnCorrelatedActive,
        setWindmillsActive,
        setPlatformsActive,
        numVerified,
        numDark,
        numUncorrelated,
        numWindmills: 0,
        numPlatforms: 0,
        tags,
        setTags,
        tagFilterStrategy,
        setTagFilterStrategy,
        openDate,
        setOpenDate,
        openConfidence,
        setOpenConfidence,
        openDetectionType,
        setOpenDetectionType,
        openStaticObjects,
        setOpenStaticObjects,
        openTags,
        setOpenTags,
        query: '',
        setQuery: () => {},
        openImageSources: false,
        setOpenImageSources: () => {},
        sentinel1Active: false,
        sentinel2Active: false,
        setSentinel1Active: () => {},
        setSentinel2Active: () => {},
    };
    const settings: Settings = {
        showProcessingStatus: false,
        setShowProcessingStatus: () => {},
        showSatelliteImage: false,
        setShowSatelliteImage: () => {},
        showEEZ: false,
        setShowEEZ: () => {},
    };
    const view: View = {
        selected,
        setSelected,
        selectedView: 'table',
        setSelectedView: () => {},
    };
    const mapView = initialMapView;
    if (isLoadingReport) {
        return <AbsoluteSpinner />;
    }
    if (isErrorReport) {
        return (
            <ErrorHandler isError={isErrorReport} errorMessage={'Loading report info failed'}>
                <VText>Error loading report info.</VText>
            </ErrorHandler>
        );
    }
    if (isSuccessReport) {
        return (
            <OverwatchContext.Provider value={{ report, filters, settings, view, mapView }}>
                <div className="flex h-screen">
                    <div className="m-2 bg-background-primary">
                        <LeftBar />
                    </div>
                    <div className="flex-grow overflow-y-auto">
                        <div className="p-2 justify-end flex">
                            <Button
                                variant="neutral"
                                size="small"
                                text={updateUrlClicked ? 'Copied URL' : 'Shareable URL'}
                                iconAfter={
                                    updateUrlClicked ? <CheckIcon size={16} color="green" /> : <ExportIcon size={16} />
                                }
                                onClick={() => {
                                    updateUrl();
                                    navigator.clipboard.writeText(window.location.href);
                                    setUpdateUrlClicked(true);
                                }}
                            />
                        </div>
                        {startDate && endDate && (
                            <ActorModeMain
                                reportUuid={reportUuid}
                                startDate={startDate}
                                endDate={endDate}
                                tags={tags}
                                data={filteredData}
                                fetchNextPage={fetchNextPage}
                                hasNextPage={hasNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                                isLoading={isLoading}
                                isError={isError}
                                error={error}
                                darkDetectionConfidence={darkDetectionConfidence}
                                updateUrl={updateUrl}
                                maxDays={maxDaysToQuery}
                                tagFilterStrategy={tagFilterStrategy}
                            />
                        )}
                    </div>
                </div>{' '}
            </OverwatchContext.Provider>
        );
    }
};
type Props = {
    reportUuid: string;
    startDate: Date;
    endDate: Date;
    tags: FilterButton[];
    data: ActorModeVessel[];
    fetchNextPage: () => void;
    hasNextPage: boolean | undefined;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    darkDetectionConfidence: number;
    updateUrl: () => void;
    maxDays: number;
    tagFilterStrategy: 'or' | 'and';
};
export const ActorModeMain: FC<Props> = ({
    reportUuid,
    startDate,
    endDate,
    tags,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    darkDetectionConfidence,
    updateUrl,
    maxDays,
    tagFilterStrategy,
}) => {
    const { mutate } = useSendToMattermost();
    const loadMoreRef = useRef(null);
    const [query, setQuery] = useState<string>('');

    useEffect(() => {
        if (isLoading || isError) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            }
        );
        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading]);
    const headers = [
        'Type',
        'Name',
        'IMO',
        'Flag & MMSI',
        'Last Updated',
        'Last Position (lat, lon)',
        'Actions last 6 months',
    ];
    const handleOnClick = (insightUuid: string) => {
        updateUrl();
        let text = `In Actor Mode: User clicked on vessel with insight uuid ${insightUuid}. `;
        text += `Link to [vessel](${window.location.origin + window.location.pathname + '/vessel/' + insightUuid}). `;
        text += `Link to [table view](${window.location.href}).`;
        mutate(text);
    };
    const filterBySearch = (row: ActorModeVessel) => {
        if (query.length > 0) {
            return (
                (row.name && row.name.toLowerCase().includes(query)) ||
                String(row.imo).includes(query) ||
                String(row.mmsi).includes(query)
            );
        }
        return true;
    };
    return (
        <div className="flex flex-col p-8 gap-8 max-w-[1536px] mx-auto">
            <ErrorHandler isError={isError} errorMessage={`Loading Vessels Failed: ${error}`}>
                <VText variant="H3">Loading Failed</VText>
                <VText>
                    {error && typeof error === 'object' && error !== null && 'message' in error
                        ? String((error as { message?: unknown }).message)
                        : 'Something went wrong on our end'}
                </VText>
            </ErrorHandler>
            <div>
                <VText variant="H1">Vessels insights: Who are they? Any interesting previous behavior?</VText>
                <VText>
                    In period: {formatRelativeTime(startDate)} -{' '}
                    {endDate > new Date() ? 'now' : formatRelativeTime(endDate)}
                </VText>
                <Link
                    to={`${getOverwatch2Url()}/${reportUuid}?startDate=${startDate
                        .toISOString()
                        .substring(0, 10)}&confidence=${darkDetectionConfidence}`}
                >
                    <Button text="Go to Map" size="small" variant="neutral" onClick={updateUrl} />
                </Link>
            </div>
            <div className="max-w-xs">
                <Search
                    placeholder={'Find insights by Name, MMSI, IMO'}
                    query={query}
                    handleChange={setQuery}
                    regexFilterOut={/[^ a-zA-Z0-9]/g}
                />
            </div>
            <div className="flex flex-col gap-7">
                <div className="grid grid-cols-7">
                    {headers.map((header) => (
                        <VText significance="tertiary" key={header} variant="D1B">
                            {header}
                        </VText>
                    ))}
                </div>
                <div className="flex flex-col gap-7 text-label-primary">
                    {isLoading && (
                        <div className="flex justify-center">
                            <Loader size={16} />
                        </div>
                    )}
                    {!isLoading && data && data.length === 0 && (
                        <NoData startDate={startDate} endDate={endDate} maxDays={maxDays} />
                    )}
                    {data &&
                        data
                            .filter(filterBySearch)
                            .map((row) => (
                                <ActorModeRow
                                    key={row.uuid}
                                    row={row}
                                    onClick={handleOnClick}
                                    reportUuid={reportUuid}
                                    filterTags={tags}
                                    tagFilterStrategy={tagFilterStrategy}
                                />
                            ))}
                </div>
            </div>
            <div ref={loadMoreRef} className="flex justify-center">
                {isFetchingNextPage && <Loader size={16} />}
                {!isFetchingNextPage && hasNextPage && (
                    <Button
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        text="Load more"
                        variant="neutral"
                    />
                )}
            </div>
        </div>
    );
};

type NoDataProps = {
    startDate: Date;
    endDate: Date;
    maxDays: number;
};
const NoData: FC<NoDataProps> = ({ startDate, endDate, maxDays }) => {
    let description;
    if (startDate > endDate) {
        description = 'Please set start date before end date';
    } else if (!isDateIntervalWithinGivenDays(startDate, endDate, maxDays)) {
        description = `Too long date interval, max period is ${maxDays} days`;
    } else {
        description = 'No data to view. Try to increase the date interval or remove some filters.';
    }
    return (
        <div className="flex justify-center">
            <VText variant="H3">{description}</VText>
        </div>
    );
};

export default ActorMode;
